import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Users, ShieldAlert, Megaphone, BookOpen, ClipboardList, ArrowRight, Cake, PartyPopper,
  Plus, MessageSquare, Sparkles, Award, Plane, UserPlus, Zap,
} from "lucide-react";
import { Link } from "react-router-dom";

const STAT_META = [
  { key: "total_employees", label: "Team Members", icon: Users, color: "var(--accent)", to: "/directory" },
  { key: "open_incidents", label: "Open Incidents", icon: ShieldAlert, color: "#ff3b5c", to: "/incidents" },
  { key: "announcements", label: "Announcements", icon: Megaphone, color: "#ffc700", to: "/announcements" },
  { key: "articles", label: "KB Articles", icon: BookOpen, color: "#00e5ff", to: "/knowledge" },
  { key: "pending_onboarding", label: "Pending Tasks", icon: ClipboardList, color: "#ff9f1c", to: "/onboarding" },
];

const QUICK_ACTIONS = [
  { key: "new-announcement", label: "New Announcement", icon: Megaphone, to: "/announcements", roles: ["admin", "manager"] },
  { key: "report-incident", label: "Report Incident", icon: ShieldAlert, to: "/incidents" },
  { key: "message-team", label: "Message Team", icon: MessageSquare, to: "/messages" },
  { key: "send-kudos", label: "Send Kudos", icon: Award, to: "/kudos" },
  { key: "request-timeoff", label: "Request Time Off", icon: Plane, to: "/timeoff" },
  { key: "ask-ai", label: "Ask AI", icon: Sparkles, to: "/knowledge" },
  { key: "add-employee", label: "Add Employee", icon: UserPlus, to: "/admin", roles: ["admin"] },
];

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [announcements, setAnnouncements] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [cel, setCel] = useState({ birthdays: [], anniversaries: [], today: {} });

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data));
    api.get("/announcements").then(({ data }) => setAnnouncements(data.slice(0, 3)));
    api.get("/incidents").then(({ data }) => setIncidents(data.slice(0, 4)));
    api.get("/celebrations").then(({ data }) => setCel(data)).catch(() => {});
  }, []);

  const monthName = cel.today?.month ? MONTHS[cel.today.month - 1] : "";
  const actions = QUICK_ACTIONS.filter((a) => !a.roles || (user?.role && a.roles.includes(user.role)));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* HERO */}
      <div className="fade-in-up">
        <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">Overview</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">
          Good to see you, {user?.first_name || user?.name?.split(" ")[0]}.
        </h1>
        <p className="text-slate-400 mt-2 text-sm">Your workspace at a glance.</p>
      </div>

      {/* QUICK ACTIONS STRIP */}
      <div className="quick-actions-strip" data-testid="quick-actions">
        <div className="flex items-center gap-2 mb-3">
          <Zap size={14} className="text-[color:var(--accent)]" />
          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Quick actions</span>
          <span className="flex-1 h-px bg-gradient-to-r from-[#2a3447]/60 to-transparent" />
        </div>
        <div className="flex flex-wrap gap-2">
          {actions.map(({ key, label, icon: Icon, to }, idx) => (
            <Link
              key={key}
              to={to}
              data-testid={`qa-${key}`}
              style={{ animationDelay: `${idx * 30}ms` }}
              className="qa-chip fade-in-up group inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-[#2a3447] bg-[#0e1119]/60 hover:bg-[color:var(--accent)]/10 hover:border-[color:var(--accent)] transition-all"
            >
              <Icon size={14} className="text-slate-400 group-hover:text-[color:var(--accent)] transition-colors" />
              <span className="text-xs font-semibold text-slate-200 group-hover:text-white transition-colors">{label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {STAT_META.map(({ key, label, icon: Icon, color, to }, idx) => (
          <Link
            to={to}
            key={key}
            data-testid={`stat-${key}`}
            style={{ animationDelay: `${idx * 60}ms`, "--kpi-color": color }}
            className="kpi-card fade-in-up group relative"
          >
            <div className="kpi-glow" aria-hidden />
            <div className="relative">
              <div className="kpi-icon-wrap">
                <Icon size={18} style={{ color }} />
              </div>
              <div className="mt-4 text-3xl font-extrabold tracking-tight">{stats[key] ?? "—"}</div>
              <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                <span>{label}</span>
                <ArrowRight size={11} className="opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" style={{ color }} />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* CELEBRATIONS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CelebrationCard
          title={`Birthdays in ${monthName}`}
          icon={<Cake size={18} />}
          accent="#ff8ac8"
          items={cel.birthdays}
          today={cel.today}
          renderLine={(p) => `${monthName} ${p.day}`}
          empty="No birthdays this month — add one from your Profile page."
          testid="birthdays-widget"
        />
        <CelebrationCard
          title={`Work anniversaries in ${monthName}`}
          icon={<PartyPopper size={18} />}
          accent="#00e5ff"
          items={cel.anniversaries}
          today={cel.today}
          renderLine={(p) => `${p.years} year${p.years > 1 ? "s" : ""} on ${monthName} ${p.day}`}
          empty="No anniversaries this month — add start dates from Admin or Profile."
          testid="anniversaries-widget"
        />
      </div>

      {/* LATEST FEEDS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="card-surface p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold">Latest Announcements</h2>
            <Link to="/announcements" className="text-xs text-[color:var(--accent)] hover:underline flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="space-y-3">
            {announcements.length === 0 && <div className="text-sm text-slate-500">No announcements yet.</div>}
            {announcements.map((a) => (
              <div key={a.id} className="border border-[#2a3447] rounded-xl p-4 hover:border-[#ff000044] transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold">{a.title}</div>
                    <div className="text-xs text-slate-400 mt-1 line-clamp-2">{a.body}</div>
                  </div>
                  <span className={`pill ${a.priority === "high" ? "bg-[#ffc70020] text-[#ffc700]" : "bg-[#242c3d] text-slate-300"}`}>{a.priority}</span>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2">by {a.author_name}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="card-surface p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold">Recent Incidents</h2>
            <Link to="/incidents" className="text-xs text-[color:var(--accent)] hover:underline flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="space-y-3">
            {incidents.length === 0 && <div className="text-sm text-slate-500">No incidents reported.</div>}
            {incidents.map((i) => (
              <div key={i.id} className="border border-[#2a3447] rounded-xl p-4 hover:border-[#ff3b5c40] transition-colors">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">{i.title}</div>
                  <span className={`pill ${i.severity === "critical" ? "bg-[#ff3b5c20] text-[#ff3b5c]" : i.severity === "high" ? "bg-[#ff9f1c20] text-[#ff9f1c]" : "bg-[#242c3d] text-slate-300"}`}>{i.severity}</span>
                </div>
                <div className="text-xs text-slate-400 mt-1 line-clamp-1">{i.description}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2">
                  {i.status.replace("_", " ")} · {i.reporter_name}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function CelebrationCard({ title, icon, accent, items, today, renderLine, empty, testid }) {
  return (
    <section className="card-surface p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <span style={{ color: accent }}>{icon}</span>
        <h2 className="text-lg font-bold">{title}</h2>
        <span className="text-xs text-slate-500">({items.length})</span>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-slate-500 py-6 text-center">{empty}</div>
      ) : (
        <ul className="space-y-3">
          {items.map((p) => {
            const isToday = today && p.day === today.day && p.month === today.month;
            const initials = (p.first_name || p.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
            return (
              <li key={p.id} className={`flex items-center gap-3 rounded-lg p-2 ${isToday ? "bg-[color:var(--accent)]/10 border border-[color:var(--accent)]/40" : ""}`}>
                {p.avatar_url ? (
                  <img src={p.avatar_url} alt={p.name} className="w-10 h-10 rounded-full object-cover border border-[#2a3447]" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#242c3d] grid place-items-center font-bold border border-[#2a3447]" style={{ color: accent }}>
                    {initials}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">
                    {p.first_name || p.name}
                    {p.discord_name && <span className="text-slate-500 font-normal"> · {p.discord_name}</span>}
                  </div>
                  <div className="text-xs text-slate-400 truncate">{p.title || p.department || ""}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-semibold" style={{ color: accent }}>{renderLine(p)}</div>
                  {isToday && <div className="text-[10px] uppercase tracking-widest text-[color:var(--accent)] mt-0.5">today 🎉</div>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
